import { urlFor } from "@/utils/client";
import { getYouTubeId } from "@/utils/constants";
import { IMember, ITeam } from "@/utils/types";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { HoverVideo } from "./hoverVideo";
import { SliderArrows } from "./sliderArrows";

export function Team(props: ITeam) {
  const members = props.members ?? [];
  const [currentSlide, setCurrentSlide] = useState(0);
  const textColor =
    props.accentColor ?? (props.darkMode ? "#fff" : "#000");

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: members.length > 1,
    breakpoints: {
      "(max-width: 768px)": {
        slides: {
          perView: members.length > 1 ? 1.15 : 1,
          spacing: 16,
          origin: "center",
        },
      },
    },
    slides: {
      perView: Math.min(
        members.length || 1,
        props.big ? 1.4 : props.showVideos ? 4.2 : 5.4,
      ),
      spacing: 30,
      origin: "center",
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel ?? 0);
    },
  });

  useEffect(() => {
    if (members.length === 0) return;
    setCurrentSlide(instanceRef.current?.track?.details?.rel ?? 0);
  }, [instanceRef, members.length]);

  return (
    <div
      className={`flex flex-col items-center component-gap w-full overflow-hidden text-center ${
        props.darkMode ? "bg-darkGrey text-white py-16 md:py-24" : ""
      }`}
    >
      <div
        style={{ color: textColor }}
        className="flex flex-col component-gap max-w-5xl px-4"
      >
        <p className="heading">{props.title}</p>
        <p className="opacity-80">{props.description}</p>
      </div>
      <div ref={sliderRef} className="keen-slider w-full">
        {members.map((member, i) => {
          return (
            <div
              key={i}
              onClick={() => {
                if (!instanceRef.current) return;
                if (i === currentSlide) return;
                const total = members.length;
                const forwardDist = (i - currentSlide + total) % total;
                const backwardDist = (currentSlide - i + total) % total;
                if (forwardDist <= backwardDist) {
                  instanceRef.current.next();
                } else {
                  instanceRef.current.prev();
                }
              }}
              className="keen-slider__slide cursor-pointer"
            >
              {props.big ? (
                <div
                  className={`flex flex-col md:flex-row items-stretch gap-8 md:gap-12 h-full text-left bg-white p-8 md:p-10 transition-all duration-300 origin-center ${
                    currentSlide !== i
                      ? "scale-[0.97] opacity-75"
                      : "scale-100 opacity-100"
                  }`}
                >
                  <MemberMedia member={member} showVideos={props.showVideos} big />
                  <div className="flex flex-col items-start justify-center gap-4 flex-1">
                    <p className="text-2xl md:text-3xl font-display text-black text-left">
                      {member.quote}
                    </p>
                    <p className="text-black text-left opacity-80">
                      {member.description}
                    </p>
                    {member.button?.link &&
                      member.button?.label &&
                      !getYouTubeId(member.button.link) && (
                      <TextualLink
                        href={member.button.link}
                        label={member.button.label}
                      />
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      {member.partnerLogo?.image ? (
                        <Image
                          className={`w-14 h-14 rounded-full object-contain p-2 ${
                            member.partnerLogoLight
                              ? "bg-darkGrey border border-white/10"
                              : "bg-white border border-black/10"
                          }`}
                          width={112}
                          height={112}
                          alt={member.partnerLogo?.caption ?? ""}
                          src={urlFor(member.partnerLogo.image).url()}
                        />
                      ) : (
                        member.image?.image && (
                          <Image
                            className="w-14 h-14 rounded-full object-cover object-top"
                            width={112}
                            height={112}
                            alt={member.image?.caption ?? ""}
                            src={urlFor(member.image.image).url()}
                          />
                        )
                      )}
                      <div className="flex flex-col gap-0.5 text-left">
                        <p className="font-display text-lg text-black">
                          {member.title}
                        </p>
                        {Boolean(
                          member.role?.trim() || member.traject?.trim()
                        ) && (
                          <p className="font-body text-lg text-black opacity-75 text-left">
                            {member.role?.trim() || member.traject?.trim()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={`gap-1 flex flex-col items-start h-full text-left transition-all duration-300 origin-center ${
                    currentSlide !== i
                      ? "scale-[0.97] opacity-75"
                      : "scale-100 opacity-100"
                  }`}
                >
                  <MemberMedia member={member} showVideos={props.showVideos} />
                  <div className="flex w-full flex-col items-start gap-0.5 text-left">
                    <p
                      style={{ color: textColor }}
                      className="font-display text-lg mt-1"
                    >
                      {member.title}
                    </p>
                    <p
                      style={{ opacity: 0.75, color: textColor }}
                      className="font-body text-lg text-left"
                    >
                      {member.role || `doet mee`}
                    </p>
                  </div>
                  {member.button?.link &&
                    member.button?.label &&
                    !getYouTubeId(member.button.link) && (
                    <TextualLink
                      href={member.button.link}
                      label={member.button.label}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <SliderArrows
        onPrev={() => instanceRef.current?.prev()}
        onNext={() => instanceRef.current?.next()}
      />
    </div>
  );
}

/**
 * The member's photo, with the video (if enabled) playing on hover and a play
 * badge that opens the full video: the Mux upload, or the YouTube link that
 * older member docs carry in their button.
 */
function MemberMedia({
  member,
  showVideos,
  big,
}: {
  member: IMember;
  showVideos?: boolean;
  big?: boolean;
}) {
  const playbackId = showVideos ? member.videoPlaybackId : null;
  const href =
    member.button?.link && getYouTubeId(member.button.link)
      ? member.button.link
      : null;

  const poster = member.image?.image ? (
    <Image
      className="absolute inset-0 w-full h-full object-cover object-center"
      width={500}
      height={667}
      alt={member.image?.caption ?? ""}
      src={urlFor(member.image.image).url()}
    />
  ) : playbackId ? (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={`https://image.mux.com/${playbackId}/thumbnail.webp?time=0.1`}
      alt={member.title}
      className="absolute inset-0 w-full h-full object-cover pointer-events-none"
    />
  ) : null;
  if (!poster) return null;

  return (
    <HoverVideo
      playbackId={playbackId}
      href={href}
      poster={poster}
      title={member.title}
      className={
        big ? "w-full md:w-72 shrink-0 aspect-[3/4]" : "w-full aspect-[3/4]"
      }
    />
  );
}

function TextualLink({ href, label }: { href: string; label: string }) {
  const inner = (
    <span className="flex items-center gap-2 italic text-sm">
      {label}
      <svg
        width="10"
        height="11"
        viewBox="0 0 10 11"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M7.24358 6.16363H0.214722V4.83655H7.24358L4.05364 1.6466L4.99997 0.714844L9.78521 5.50009L4.99997 10.2853L4.05364 9.35357L7.24358 6.16363Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );

  if (href.startsWith("http")) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {inner}
      </a>
    );
  }
  return <Link href={href}>{inner}</Link>;
}
